/**
 * The sign-in's second step for a user who has one (a 202 `mfa_required` from POST /v2/edit/login/;
 * apiv2/dashboard_auth.openapi.yaml): an authenticator-app code, a texted code or a recovery code. Rendered
 * inside SignIn, so the tenant card and the intended path stay. Ported from the console's MFAChallengePage.
 *
 * Opens on the user's default method and offers only the methods the pending token names. A text message goes
 * out only when the user asks for it ("Send code"); "Resend" waits `resend_after`. Five wrong codes burn the
 * pending token: then only "Start again" is left.
 */
import { Alert, Box, Button, FormControl, FormControlLabel, Link, Radio, RadioGroup, Typography } from '@mui/material';
import React from 'react';

import { SIGN_IN_UNREACHABLE } from '../store/contextStore';
import { describeRefusal, sendSignInCode, verifySignIn } from './mfaApi';
import { CodeField, codeReady, METHOD_LABELS, useCountdown } from './mfaUi';

export default function MfaCodeStep({ pending, onSignedIn, onRestart }) {
    const methods = (pending.methods || []).filter((m) => m in METHOD_LABELS);
    const [method, setMethod] = React.useState(methods.includes(pending.default_method) ? pending.default_method : methods[0]);
    const [code, setCode] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    // The pending token is gone (expired, burned by wrong codes): nothing works but "Start again".
    const [spent, setSpent] = React.useState(false);
    const [sentTo, setSentTo] = React.useState(null);
    const [resendIn, startResend] = useCountdown();

    const recovery = method === 'recovery_code';

    const pickMethod = (next) => {
        setMethod(next);
        setCode('');
        setError(null);
    };

    const sendCode = async () => {
        setBusy(true);
        setError(null);
        try {
            const { status, body } = await sendSignInCode(pending.mfa_pending_token);
            if (status === 200) {
                setSentTo(body?.sent_to_masked || pending.sms_to_masked || 'your phone');
                startResend(body?.resend_after);
            } else {
                if (status === 401) {
                    setSpent(true);
                }
                if (body?.code === 'sms_cooldown') {
                    startResend(body.retry_after);
                }
                setError(describeRefusal(body, 'The code could not be sent. Try again.'));
            }
        } catch (err) {
            setError(SIGN_IN_UNREACHABLE);
        }
        setBusy(false);
    };

    const verify = async (ev) => {
        ev.preventDefault();
        if (busy || spent || !codeReady(code, recovery)) {
            return;
        }
        setBusy(true);
        setError(null);
        let answer;
        try {
            answer = await verifySignIn(pending.mfa_pending_token, method, code.trim());
        } catch (err) {
            setError(SIGN_IN_UNREACHABLE);
            setBusy(false);
            return;
        }
        const { status, body } = answer;
        if (status === 201 || status === 200) {
            try {
                // Signs in and leaves the page (or moves SignIn on): nothing to update here.
                await onSignedIn(body, method);
            } catch (err) {
                // The pending token is used up; the session could not be stored.
                setError(SIGN_IN_UNREACHABLE);
                setSpent(true);
                setBusy(false);
            }
            return;
        }
        if (status === 401 && (body?.code === 'mfa_pending_expired' || body?.attempts_left === 0)) {
            setSpent(true);
        }
        setCode('');
        setError(describeRefusal(body, 'The code could not be checked. Try again.'));
        setBusy(false);
    };

    let intro;
    if (method === 'totp') {
        intro = 'Enter the 6-digit code from your authenticator app.';
    } else if (method === 'sms') {
        intro = sentTo
            ? `We sent a code to ${sentTo}. It works for 5 minutes.`
            : `We will text a code to ${pending.sms_to_masked || 'your phone'}.`;
    } else {
        intro = 'Enter one of your recovery codes. Each code works once.';
    }

    let sendLabel = sentTo ? 'Resend code' : 'Send code';
    if (resendIn > 0) {
        sendLabel = `${sentTo ? 'Resend' : 'Send'} in ${resendIn} s`;
    }

    return (
        <Box component="form" noValidate onSubmit={verify} sx={{ width: '100%', mt: 2 }}>
            <Typography variant="h6" component="h2">Two-step sign-in</Typography>
            {methods.length > 1 && (
                <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <RadioGroup row aria-label="Sign-in method" value={method} onChange={(ev) => pickMethod(ev.target.value)}>
                        {methods.map((m) => (
                            <FormControlLabel key={m} value={m} control={<Radio size="small" />} label={METHOD_LABELS[m]} disabled={busy} />
                        ))}
                    </RadioGroup>
                </FormControl>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{intro}</Typography>
            {method === 'sms' && (
                <Button variant="outlined" sx={{ mt: 1 }} onClick={sendCode} disabled={busy || spent || resendIn > 0}>
                    {sendLabel}
                </Button>
            )}
            <CodeField key={method} recovery={recovery} value={code} onChange={setCode} autoFocus disabled={spent} />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 2 }} disabled={busy || spent || !codeReady(code, recovery)}>
                Verify
            </Button>
            <Box sx={{ mt: 1, textAlign: 'right' }}>
                <Link component="button" type="button" variant="body2" onClick={onRestart}>
                    Start again
                </Link>
            </Box>
        </Box>
    );
}
